/**
 * storageFileName - Sanitização de nomes de arquivo para chaves do Supabase Storage
 *
 * PROBLEMA QUE RESOLVE:
 * O Supabase Storage rejeita chaves (paths) com caracteres fora de um conjunto
 * simples, retornando o erro "Invalid key". Nomes de arquivo enviados pelos
 * usuários frequentemente contêm emojis (ex.: "🎯Relatorio.pdf"), acentos,
 * espaços e símbolos, o que quebrava o upload de anexos em vários módulos
 * (tarefas, negócios, atividades, veículos, clientes, etc.).
 *
 * IMPORTANTE: esta função sanitiza APENAS a chave física no Storage.
 * O nome original do arquivo continua sendo gravado no banco (coluna file_name)
 * e é o que o usuário vê na interface.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-21 v1.0.0 - Criação: utilitário único usado por todos os
 *            hooks de anexo, removendo emojis e qualquer caractere não-ASCII.
 */

/** Tamanho máximo do nome (sem extensão) na chave do Storage */
const MAX_NAME_LENGTH = 100;

/** Tamanho máximo da extensão considerada válida (ex.: .xlsx) */
const MAX_EXT_LENGTH = 10;

/**
 * Converte um nome de arquivo em uma chave segura para o Supabase Storage.
 *
 * Regras aplicadas:
 * 1. Remove acentos (normalização NFD + remoção de diacríticos);
 * 2. Troca espaços por underscore;
 * 3. Remove emojis e qualquer caractere fora de [A-Za-z0-9._-];
 * 4. Colapsa separadores repetidos e limpa bordas;
 * 5. Limita o tamanho e garante fallback "arquivo";
 * 6. Mantém a extensão original em minúsculas (quando válida).
 *
 * @param fileName Nome original do arquivo (pode conter emoji/acentos)
 * @returns Nome seguro para compor o storage_path
 */
export function sanitizeStorageFileName(fileName: string): string {
  const safeInput = (fileName || '').trim();

  // Separa nome e extensão
  const lastDot = safeInput.lastIndexOf('.');
  const hasExt = lastDot > 0 && safeInput.length - lastDot - 1 <= MAX_EXT_LENGTH;
  const rawName = hasExt ? safeInput.substring(0, lastDot) : safeInput;
  const rawExt = hasExt ? safeInput.substring(lastDot + 1) : '';

  // Normaliza e mantém somente caracteres ASCII seguros
  const sanitizedName = rawName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/\s+/g, '_') // espaços -> underscore
    .replace(/[^a-zA-Z0-9._-]/g, '_') // remove emojis e demais símbolos
    .replace(/\.{2,}/g, '.') // evita ".." no path
    .replace(/_+/g, '_') // colapsa underscores
    .replace(/^[._-]+|[._-]+$/g, '') // limpa bordas
    .substring(0, MAX_NAME_LENGTH);

  // Extensão também precisa ser ASCII simples
  const sanitizedExt = rawExt
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

  const base = sanitizedName || 'arquivo';
  return sanitizedExt ? `${base}.${sanitizedExt}` : base;
}

export default sanitizeStorageFileName;
